import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import client from "../../lib/api";
import { CAT_LABEL } from "../../lib/categories";
import { formatDate } from "../../lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Eye, Upload, Download } from "lucide-react";

const FILTERS = [
  { label: "All", params: {} },
  { label: "Published", params: { status: "published" } },
  { label: "Drafts", params: { status: "draft" } },
  { label: "Scheduled", params: { status: "scheduled" } },
  { label: "Breaking", params: { flag: "breaking" } },
  { label: "Featured", params: { flag: "featured" } },
  { label: "Investigation", params: { flag: "investigation" } },
  { label: "Trending", params: { flag: "trending" } },
  { label: "Video News", params: { flag: "video" } },
  { label: "Most Read", params: { sort: "views" } },
  { label: "Duplicates", params: { status: "duplicate" } },
];

export default function ArticlesAdmin() {
  const [items, setItems] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status") || "";
  const flag = searchParams.get("flag") || "";
  const sort = searchParams.get("sort") || "";

  const load = () => client.get("/admin/articles", {
    params: { status: status || undefined, flag: flag || undefined, sort: sort || undefined, limit: 100 },
  }).then((r) => setItems(r.data.items));

  useEffect(() => { setItems(null); load(); /* eslint-disable-next-line */ }, [status, flag, sort]);

  const del = async (id) => {
    if (!window.confirm("Delete this article permanently?")) return;
    await client.delete(`/admin/articles/${id}`);
    toast.success("Article deleted");
    load();
  };
  const setStatus = async (id, action) => {
    try {
      await client.post(`/admin/articles/${id}/${action}`);
      toast.success(action === "publish" ? "Article published" : "Article unpublished (moved to drafts)");
      load();
    } catch (err) { toast.error("Failed: " + (err.response?.data?.detail || err.message)); }
  };

  const isActive = (p) => (p.status || "") === status && (p.flag || "") === flag && (p.sort || "") === sort;

  return (
    <div data-testid="admin-articles">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-head font-bold text-3xl uc">Articles</h1>
        <Link to="/admin/articles/new" className="bg-r24-red text-white font-bold uc text-sm px-5 py-2.5" data-testid="articles-new-btn">+ New</Link>
      </div>
      <div className="flex gap-2 mb-5 flex-wrap">
        {FILTERS.map((f) => (
          <button key={f.label} onClick={() => setSearchParams(f.params)}
            data-testid={`filter-${f.label.toLowerCase().replace(/ /g, "-")}`}
            className={`font-head font-semibold uc text-xs px-4 py-2 border-2 ${isActive(f.params) ? "bg-black text-white border-black" : "border-gray-300 hover:border-black"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {!items ? <p>Loading…</p> : items.length === 0 ? <p className="text-gray-500" data-testid="articles-empty">No articles in this view.</p> : (
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left font-head uc text-xs text-gray-500">
                <th className="p-3">Title</th><th className="p-3">Category</th><th className="p-3">Status</th><th className="p-3">Views</th><th className="p-3">Date</th><th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`admin-article-row-${a.id}`}>
                  <td className="p-3 max-w-xs">
                    <div className="font-semibold line-clamp-1">{a.title}</div>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {a.breaking && <span className="text-[10px] bg-r24-red text-white px-1 uc">Breaking</span>}
                      {a.featured && <span className="text-[10px] bg-black text-white px-1 uc">Featured</span>}
                      {a.video_url && <span className="text-[10px] bg-blue-600 text-white px-1 uc">Video</span>}
                    </div>
                  </td>
                  <td className="p-3 text-gray-600">{CAT_LABEL[a.category]?.name || a.category}</td>
                  <td className="p-3"><span className={`text-[10px] uc font-bold px-2 py-0.5 ${a.status === "published" ? "bg-green-100 text-green-700" : a.status === "duplicate" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}`}>{a.status}</span></td>
                  <td className="p-3 text-gray-600">{a.views}</td>
                  <td className="p-3 text-gray-500">{formatDate(a.published_at)}</td>
                  <td className="p-3">
                    <div className="flex gap-2 items-center">
                      <a href={`/article/${a.slug}`} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-black" title="View"><Eye size={16} /></a>
                      <Link to={`/admin/articles/${a.id}`} className="text-blue-600" title="Edit" data-testid={`edit-${a.id}`}><Pencil size={16} /></Link>
                      {a.status === "published"
                        ? <button onClick={() => setStatus(a.id, "unpublish")} className="text-amber-600" title="Unpublish" data-testid={`unpublish-${a.id}`}><Download size={16} /></button>
                        : <button onClick={() => setStatus(a.id, "publish")} className="text-green-600" title="Publish" data-testid={`publish-${a.id}`}><Upload size={16} /></button>}
                      <button onClick={() => del(a.id)} className="text-r24-red" title="Delete" data-testid={`delete-${a.id}`}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
